import { Inject, Injectable, OnDestroy } from '@angular/core';
import { defaultTxt, simpleValueValidation } from '@miq/utils';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { InterpolationFillerService } from './interpolation-filler.service';
import { BlueprintBase, ColumnBase, GroupBase, InterpolatedBlueprint, Layout } from './models/blueprint.model';
import { DataObject, ReportInfo, ReportOptions, ReportRecord } from './models/report.model';
import {
  DisplayFnArgumentsInfo,
  LayoutAndRecords,
  MappedTemplateField,
  PropertyGroup,
  RequiredFieldsFromColumn,
  TemplateField,
  TemplateFieldsCatalog,
} from './models/template.model';
import { ReportExportViewService } from './report-export-view.service';
import { ReportFieldFormatterService } from './report-field-formatter.service';
import { PropertyGroupService } from './report-group.service';
import { AVOID_API_FOR_COLUMNS } from './shared/constants/uris.const';
import { disclaimerFont, disclaimerLang, LocaleKeys } from './shared/templates/utils/template-display-sort-order.const';
import { EntityType } from './shared/constants/entity.const';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

const disclaimerLabel = `disclaimer`;
@Injectable()
export class InterpolatedBlueprintService implements OnDestroy {
  private _interpolationsByTemplateId: {
    [templateId: number]: { [layout: string]: BehaviorSubject<InterpolatedBlueprint> };
  } = {};
  private _entity: EntityType;

  constructor(
    @Inject(CbreLoggerToken) private readonly logger: CbreLogger,
    private reportFieldFormatterService: ReportFieldFormatterService,
    private reportExportViewService: ReportExportViewService,
    private interpolationFillerService: InterpolationFillerService,
    private propertyGroupService: PropertyGroupService
  ) {}

  private resetInterpolationsByTemplateId(): void {
    Object.values(this._interpolationsByTemplateId).forEach(layout => {
      Object.values(layout).forEach(interpolatedBluePrint => interpolatedBluePrint.complete());
    });
    this._interpolationsByTemplateId = {};
  }

  ngOnDestroy(): void {
    this.resetInterpolationsByTemplateId();
  }

  isLayoutExist(templateId: number, layout: string): boolean {
    return (
      this._interpolationsByTemplateId[templateId] != null &&
      this._interpolationsByTemplateId[templateId][layout] != null
    );
  }

  fetchInterpolationByTemplateId(templateId: number, layout: Layout): InterpolatedBlueprint {
    return this.getInterpolationByTemplateId(templateId, layout).value;
  }

  fetchInterpolationByTemplateId$(templateId: number, layout: Layout): Observable<InterpolatedBlueprint> {
    return this.getInterpolationByTemplateId(templateId, layout).asObservable();
  }

  setInterpolationByTemplateId(
    templateId: number,
    interpolatedBlueprint: InterpolatedBlueprint,
    layout: Layout,
    currentEntity: EntityType
  ) {
    if (this._entity !== currentEntity) {
      if (this._entity) {
        this.resetInterpolationsByTemplateId();
      }
      this._entity = currentEntity;
    }
    this.getInterpolationByTemplateId(templateId, layout).next(interpolatedBlueprint);
  }

  // BehaviorSubject kept: dynamic keyed cache (templateId × layout) — signals can't
  // model a Map<K, Signal<V>> created on demand without losing multi-subscriber semantics.
  private getInterpolationByTemplateId(templateId: number, layout: Layout) {
    if (this._interpolationsByTemplateId[templateId] == null) {
      const _layout = {};
      _layout[layout] = new BehaviorSubject<InterpolatedBlueprint>(null);
      this._interpolationsByTemplateId[templateId] = _layout;
    } else if (this._interpolationsByTemplateId[templateId][layout] == null) {
      this._interpolationsByTemplateId[templateId][layout] = new BehaviorSubject<InterpolatedBlueprint>(null);
    }
    return this._interpolationsByTemplateId[templateId][layout];
  }

  getImages(reportId: number) {
    return this.reportExportViewService.getImages(reportId);
  }

  mapInterpolatedBlueprint(
    blueprint: BlueprintBase,
    templateCatalog: TemplateFieldsCatalog,
    entityCatalog: { [entity: string]: { [columnId: number]: any } },
    currentEntity: string,
    localeId?: LocaleKeys
  ): InterpolatedBlueprint {
    const newTemplateCatalog = this.transformEntityCatalogIntoMap(entityCatalog, currentEntity);
    return Object.keys(blueprint).map(key => {
      const groups: Array<GroupBase<TemplateField>> = this.transformBlueprintColumnsIntoGroups(
        blueprint[key],
        templateCatalog,
        newTemplateCatalog,
        localeId
      );
      return groups;
    }, []);
  }

  private transformBlueprintColumnsIntoGroups(
    column: ColumnBase,
    templateCatalog: TemplateFieldsCatalog,
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    localeId?: LocaleKeys
  ): Array<GroupBase<TemplateField>> {
    return Object.values(column || ({} as ColumnBase)).map((group: GroupBase) => {
      const fields = this.interpolateGroupFieldsWithTemplateFieldsCatalog(
        group.fields,
        templateCatalog,
        newTemplateCatalog,
        localeId
      );
      return { ...group, fields };
    });
  }

  private interpolateGroupFieldsWithTemplateFieldsCatalog(
    fields: Array<{ id: number; [p: string]: any }>,
    templateCatalog: TemplateFieldsCatalog,
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    localeId?: LocaleKeys
  ) {
    /*
     * "fields" being null or undefined means that one of the groups of the
     * currently selected blueprint is missing the "fields" attribute
     */
    if (fields == null) {
      throw new Error(`Current Blueprint group is missing the fields attribute`);
    } else {
      return fields.map<TemplateField>(field => {
        const templateField: TemplateField = templateCatalog[field.id];
        if (field.label === disclaimerLabel) {
          field.value = disclaimerLang[localeId];
          field.options.fontFace = disclaimerFont[localeId];
        }
        let displayFnArguments: DisplayFnArgumentsInfo[] = [];
        if (templateField != null) {
          displayFnArguments = this.interpolateDisplayFnArgumentsWithEntityCatalogColumns(
            templateField.displayFnArguments,
            newTemplateCatalog,
            field.id
          );
        }
        return {
          ...field,
          ...templateField,
          displayFnArguments,
        };
      });
    }
  }

  private interpolateDisplayFnArgumentsWithEntityCatalogColumns(
    displayFnArguments: DisplayFnArgumentsInfo[],
    newTemplateCatalog: { [p: number]: DisplayFnArgumentsInfo },
    fieldId: number
  ): DisplayFnArgumentsInfo[] {
    return displayFnArguments.map(originalDisplayFnArgument => {
      const mappedDisplayFnArgument = newTemplateCatalog[originalDisplayFnArgument.id];
      const mappedDisplayFnUOMArgument =
        originalDisplayFnArgument.uomId != null ? newTemplateCatalog[originalDisplayFnArgument.uomId] : null;
      if (
        mappedDisplayFnArgument == null ||
        (originalDisplayFnArgument.uomId != null && mappedDisplayFnUOMArgument == null)
      ) {
        const warnMessage =
          mappedDisplayFnArgument == null
            ? `Argument ${originalDisplayFnArgument.id} not found in entity catalog for field ${fieldId}`
            : '';
        const uomMissing =
          mappedDisplayFnUOMArgument == null
            ? `Argument UOM ${originalDisplayFnArgument.uomId} not found in entity catalog for field ${fieldId}`
            : '';
        this.logger.info(warnMessage);
      }
      return { ...originalDisplayFnArgument, ...mappedDisplayFnArgument, uomArgument: mappedDisplayFnUOMArgument };
    });
  }

  /**
   * As per current implementation, entity catalog is getting input in an object
   * structure, using the path as attribute name. Interpolated Blueprint
   * requires the same structure, but using the column ID as attribute key
   * instead.
   *
   * @param entityCatalog
   * @param currentEntity
   */
  private transformEntityCatalogIntoMap(
    entityCatalog: { [entity: string]: { [field: string]: { [att: string]: any } } } = {},
    currentEntity: string
  ): { [columnId: number]: DisplayFnArgumentsInfo } {
    const result = Object.entries(entityCatalog).reduce<{ [columnId: number]: DisplayFnArgumentsInfo }>(
      (acc, [entity, mappedEntityColumns]) => {
        const flatMappedColumnsById = (Object.values(mappedEntityColumns) as any[]).reduce<{
          [columnId: number]: RequiredFieldsFromColumn;
        }>(
          // TECH DEBT (Ramakrishna): Column Info Shape/Signature enhancement.
          (acc, column) => ({
            ...acc,
            [column.columnId]: {
              id: column.columnId,
              field: column.field,
              path: column.path,
              dataType: column.dataType,
              dataFormat: column.options && column.options.format,
              isUpdatable:
                !column.field.includes('/') && // not updatable if field is inside an array
                currentEntity === entity &&
                !!(column.parent && column.parent.update) &&
                column.condition == null,
              condition: column.condition,
              lovValues:
                column.lookupCategory != null && column.id !== AVOID_API_FOR_COLUMNS
                  ? this.reportFieldFormatterService.getLovValues(column.lookupCategory, column.columnId)
                  : null,
            },
          }),
          {}
        );
        return { ...acc, ...flatMappedColumnsById };
      },
      {}
    );
    return result;
  }

  flattenBlueprintColumnsToGroups(blueprint: InterpolatedBlueprint): Array<GroupBase<TemplateField>> {
    return [].concat(...blueprint);
  }

  fillInterpolationWithReportMainData(
    report: ReportInfo,
    stringInjectorMap?: { [key: string]: string | Function },
    layoutType?: Layout,
    templateId?: number
  ): InterpolatedBlueprint<MappedTemplateField> {
    let interpolatedDate: InterpolatedBlueprint;
    interpolatedDate = this.fetchInterpolationByTemplateId(templateId, layoutType);
    if (layoutType === Layout.Cover && interpolatedDate == null) {
      return null;
    }

    return interpolatedDate.map(column =>
      column.map(group => {
        const fields = group.fields.map(field =>
          this.mapFieldWithReportMainData(field, report, templateId, stringInjectorMap)
        );
        return { ...(group as GroupBase<TemplateField>), fields };
      })
    );
  }

  private mapFieldWithReportMainData(
    field: any,
    report: ReportInfo,
    templateId?: number,
    stringInjectorMap?: { [key: string]: string | Function }
  ) {
    const { id, label, options, value: fieldValue, reportAttribute } = field;
    let reportValue: string | string[] = fieldValue;
    if (options && options.checkValue) {
      const result: boolean = report.records.every(record =>
        this.reportFieldFormatterService.isFieldVisible(record, { templateId, field })
      );
      reportValue = result ? reportValue : '';
    }

    if (fieldValue === 'PREPARED FOR: ' || fieldValue == null) {
      const attributeValue = decodeURIComponent(report[reportAttribute] as string);
      reportValue =
        (fieldValue === undefined ? '' : reportValue + ' ') +
        `${simpleValueValidation(attributeValue) ? attributeValue.toString().toUpperCase() : defaultTxt}`;
    }

    if (stringInjectorMap != null && !Array.isArray(reportValue)) {
      reportValue = reportValue.replace(/\${([^}]+)}/g, (_, prop) => {
        const injector = stringInjectorMap[prop];
        let value = injector.toString();
        if (typeof injector === 'function') {
          value = injector();
        }
        return value;
      });
    }

    return options != null ? { id, label, value: of(reportValue), options } : { id, label, value: of(reportValue) };
  }

  fillInterpolationWithReportData(
    records: ReportRecord[],
    templateId: number,
    settings?: Partial<{ joinString: boolean; onlyUrl: boolean; symbols: DataObject; reportOptions?: ReportOptions }>,
    stringInjectorMap?: { [key: string]: string | Function },
    reportId?: number
  ): InterpolatedBlueprint<MappedTemplateField>[] {
    const recordsInterpolations = records.map(record => {
      const layout = record.layout || Layout.MainLayout;
      const interpolationByTemplateId = this.fetchInterpolationByTemplateId(templateId, layout);
      const interpolationWithData = interpolationByTemplateId.map(column => {
        const pptFieldsOnly = column.filter(field => !field.filterOutInPPT);
        return pptFieldsOnly.map(group => {
          return this.interpolationFillerService.fillInterpolation(
            group,
            record,
            settings,
            templateId,
            stringInjectorMap,
            reportId
          );
        });
      });
      return interpolationWithData;
    });
    return recordsInterpolations;
  }

  /**
   * @todo this service is mainly used by the ppt-generator.service (MIQ) and
   *   we might need to review if we need to refactor it.
   *
   * @param templateId
   * @param mapAndRecords
   * @param reportId
   * @param settings
   * @param stringInjectorMap
   */
  fillInterpolationWithTemplateId(
    templateId: number,
    mapAndRecords: LayoutAndRecords[],
    reportId: number,
    settings?: Partial<{
      reportOptions: ReportOptions;
      joinString: boolean;
      onlyUrl: boolean;
      symbols: DataObject;
      recordsLength?: number;
    }>,
    stringInjectorMap?: { [key: string]: string | Function }
  ): InterpolatedBlueprint<MappedTemplateField>[] {
    const recordsInterpolations = mapAndRecords.map(itemForSlides =>
      this.interpolateForSlide(templateId, itemForSlides, reportId, settings, stringInjectorMap)
    );
    return recordsInterpolations;
  }

  private interpolateForSlide(
    templateId: number,
    itemForSlides: LayoutAndRecords,
    reportId: number,
    settings?: Partial<{
      reportOptions: ReportOptions;
      joinString: boolean;
      onlyUrl: boolean;
      symbols: DataObject;
      recordsLength?: number;
    }>,
    stringInjectorMap?: { [key: string]: string | Function }
  ): GroupBase<MappedTemplateField>[][] {
    let restrictMapCallToOne = false;
    return this.fetchInterpolationByTemplateId(templateId, itemForSlides.layout).map(groups => {
      const groupData: GroupBase<MappedTemplateField>[] = [];
      itemForSlides.splitArray.forEach(record => {
        this.processGroupsForInterpolation(
          groups,
          record,
          settings,
          templateId,
          stringInjectorMap,
          reportId,
          groupData,
          () => restrictMapCallToOne,
          v => {
            restrictMapCallToOne = v;
          }
        );
      });
      return groupData;
    });
  }

  private processGroupsForInterpolation(
    groups: any[],
    record: any,
    settings: any,
    templateId: number,
    stringInjectorMap: { [key: string]: string | Function },
    reportId: number,
    groupData: any[],
    getRestrictMapCallToOne: () => boolean,
    setRestrictMapCallToOne: (v: boolean) => void
  ) {
    groups
      .filter(field => !field.filterOutInPPT)
      .forEach(group => {
        if (getRestrictMapCallToOne() && group.isSummaryMapImage) {
          return;
        }
        if (group.isSummaryMapImage) {
          setRestrictMapCallToOne(true);
        }
        const interpolateDataFromHelper = this.interpolationFillerService.fillInterpolation(
          group,
          record,
          settings,
          templateId,
          stringInjectorMap,
          reportId
        );
        groupData.push(interpolateDataFromHelper);
      });
  }

  setReportPropertyGroups(res: PropertyGroup[]) {
    this.propertyGroupService.setReportPropertyGroups(res);
  }

  getReportPropertyGroups() {
    return this.propertyGroupService.getReportPropertyGroups();
  }

  getReportPropertyGroupsStream() {
    return this.propertyGroupService.getReportPropertyGroupsStream();
  }
}
